import {useEffect, useState} from 'react';
import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {isEmpty} from 'lodash';
import LFCChartsInputScaleDialog, {
  initialScaleValue,
  ScaleValue
} from './components/LFCChartsInputScale';
import {useTranslation} from 'react-i18next';

/**
 * 引数
 */
interface Props {
  title: string;
  source: any;
  x?: {
    type: 'time' | 'value' | 'category';
    dsColumn: string;
  };
  y?: {
    type: 'time' | 'value' | 'category';
    dsColumn: string;
  };
  xAxisName?: string;
  yAxisName?: string;
  series?: string;
  stacked?: boolean; //付与する事で積み上げになります
  grid?: {
    top?: number | string;
    bottom?: number | string;
    left?: number | string;
    right?: number | string;
  };
  color?: string | {};
  exportData?: any;
  exportFields?: any;
  exportFilename?: string;
  height?: string;
  bottom?: string;
  top?: string;
  onClick?: (prm: {data: any}) => void;
  mapName?: string;
  refJobTitle?: string;
  parameterName?: string;
  inputScale?: boolean;
  xlist: any;
  isShowDetailAnalysis?: boolean;
  onClickDetailAnalysis?: any;
  zoomStatus?: boolean;
  zoomCharts?: any;
  zoomChartsClose?: any;
  zoomChartId?: string;
  zoomedChartId?: string;
}

/**
 * LFCChartsBar
 * @param props
 * @returns
 */
const LFCChartsBoxXYZ1 = (props: Props) => {
  const {t} = useTranslation();
  const [scaleValue, setScaleValue] = useState<ScaleValue>(initialScaleValue);
  const [inputScaleOpen, setInputScaleOpen] = useState(false);
  const [boxData, setBoxData] = useState([]);

  // スケール変更
  const onApply = (scaleValue: ScaleValue) => {
    setScaleValue(scaleValue);
  };

  const makeBoxData = () => {
    //ボックスデータの算出
    const CalcQuartile = (data: any) => {
      let max = Math.max(...data.map((a: any) => Number(a)));
      let min = Math.min(...data.map((a: any) => Number(a)));

      const dataQ1 = () => {
        let data_s: any = data.sort((a: number, b: number) => a - b);
        let pos = (data_s.length - 1) * 0.25;
        let base = Math.floor(pos);
        let rest = pos - base;
        if (data_s[base + 1] !== undefined) {
          return data_s[base] + rest * (data_s[base + 1] - data_s[base]);
        } else {
          return data_s[base];
        }
      };

      const dataQ2 = () => {
        let data_s: any = data.sort((a: number, b: number) => a - b);
        let pos = (data_s.length - 1) * 0.5;
        let base = Math.floor(pos);
        let rest = pos - base;
        if (data_s[base + 1] !== undefined) {
          return data_s[base] + rest * (data_s[base + 1] - data_s[base]);
        } else {
          return data_s[base];
        }
      };

      const dataQ3 = () => {
        let data_s: any = data.sort((a: number, b: number) => a - b);
        let pos = (data_s.length - 1) * 0.75;
        let base = Math.floor(pos);
        let rest = pos - base;
        if (data_s[base + 1] !== undefined) {
          return data_s[base] + rest * (data_s[base + 1] - data_s[base]);
        } else {
          return data_s[base];
        }
      };
      // [min,  Q1,  median (or Q2),  Q3,  max]
      return [min, dataQ1(), dataQ2(), dataQ3(), max];
    };

    let tmp_x: any = [];
    let tmp_y: any = [];
    let tmp_z: any = [];
    let tmpOutPut: any = [];
    tmpOutPut[0] = [];
    tmpOutPut[1] = [];
    tmpOutPut[2] = [];

    if (!isEmpty(props.xlist)) {
      props.xlist.forEach((a: string) => {
        if (props.source.xlist.find((k: string) => k === a)) {
          props.source.xlist.find((b: string, index: number) => {
            if (b === a) {
              tmp_x.push(props.source.data_x[index]);
              tmp_y.push(props.source.data_y[index]);
              tmp_z.push(props.source.data_z[index]);
            }
          });
        } else {
          tmp_x.push([]);
          tmp_y.push([]);
          tmp_z.push([]);
        }
      });

      tmp_x.forEach((a: any) => {
        if (!isEmpty(a)) {
          tmpOutPut[0].push(CalcQuartile(a));
        } else {
          tmpOutPut[0].push([]);
        }
      });
      tmp_y.forEach((a: any) => {
        if (!isEmpty(a)) {
          tmpOutPut[1].push(CalcQuartile(a));
        } else {
          tmpOutPut[1].push([]);
        }
      });
      tmp_z.forEach((a: any) => {
        if (!isEmpty(a)) {
          tmpOutPut[2].push(CalcQuartile(a));
        } else {
          tmpOutPut[2].push([]);
        }
      });
    } else {
      props.source.xlist.find((b: string, index: number) => {
        tmpOutPut[0].push(CalcQuartile([props.source.data_x[index]]));
        tmpOutPut[1].push(CalcQuartile([props.source.data_y[index]]));
        tmpOutPut[2].push(CalcQuartile([props.source.data_z[index]]));
      });
    }
    setBoxData(tmpOutPut);
  };

  const [exportData, setExportData] = useState([]);

  useEffect(() => {
    makeBoxData();
  }, [props]);

  useEffect(() => {
    let tmp: any = [];
    if (!isEmpty(boxData) && !isEmpty(props.xlist)) {
      props.xlist.forEach((a: any, index: number) => {
        tmp.push({
          work: String(
            Array.from(
              new Set(
                props.exportData.filter((a: any) => a.map === props.mapName).map((b: any) => b.work)
              )
            )
          ),
          deviceid: String(
            Array.from(
              new Set(
                props.exportData
                  .filter((a: any) => a.map === props.mapName)
                  .map((b: any) => b.deviceid)
              )
            )
          ),
          map: props.mapName,
          for_export: a,
          x0: boxData[0][index][4],
          x1: boxData[0][index][3],
          x2: boxData[0][index][2],
          x3: boxData[0][index][1],
          x4: boxData[0][index][0],
          y0: boxData[1][index][4],
          y1: boxData[1][index][3],
          y2: boxData[1][index][2],
          y3: boxData[1][index][1],
          y4: boxData[1][index][0],
          z0: boxData[2][index][4],
          z1: boxData[2][index][3],
          z2: boxData[2][index][2],
          z3: boxData[2][index][1],
          z4: boxData[2][index][0]
        });
      });
      setExportData(tmp);
    } else if (!isEmpty(boxData) && isEmpty(props.xlist)) {
      props.source.xlist.forEach((a: any, index: number) => {
        tmp.push({
          work: String(
            Array.from(
              new Set(
                props.exportData.filter((a: any) => a.map === props.mapName).map((b: any) => b.work)
              )
            )
          ),
          deviceid: String(
            Array.from(
              new Set(
                props.exportData
                  .filter((a: any) => a.map === props.mapName)
                  .map((b: any) => b.deviceid)
              )
            )
          ),
          map: props.mapName,
          for_export: props.exportData[index] !== undefined ? props.exportData[index].lq_time : '',
          data_x: props.exportData[index] !== undefined ? props.source.data_x[index] : '',
          data_y: props.exportData[index] !== undefined ? props.source.data_y[index] : '',
          data_z: props.exportData[index] !== undefined ? props.source.data_z[index] : '',
          serial: props.exportData[index] !== undefined ? props.exportData[index].serial : ''
        });
      });
      setExportData(tmp);
    }
  }, [boxData]);

  return (
    <div>
      <LFCBaseCharts
        option={{
          title: {
            text: props.title
          },
          legend: {
            id: 1,
            type: 'scroll',
            itemWidth: 10,
            show: true,
            top: '90%',
            textStyle: {
              fontSize: 10
            }
          },
          tooltip: {
            trigger: 'item',
            formatter: function (params: any) {
              if (params.seriesType === 'boxplot') {
                // [min,  Q1,  median (or Q2),  Q3,  max]
                return (
                  params.name +
                  ':' +
                  params.seriesName +
                  '<br/>' +
                  t('最大') +
                  ':' +
                  Number(params.value[5]).toFixed(2) +
                  '<br/>' +
                  'Q3：' +
                  Number(params.value[4]).toFixed(2) +
                  '<br/>' +
                  t('中央') +
                  ' (or Q2)：' +
                  Number(params.value[3]).toFixed(2) +
                  '<br/>' +
                  'Q1：' +
                  Number(params.value[2]).toFixed(2) +
                  '<br/>' +
                  t('最小') +
                  ':' +
                  Number(params.value[1]).toFixed(2) +
                  `
                  <svg version="1.1" id="_x32_" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 512 512" style="width: 20px; height: 20px; opacity: 1; position: absolute; right: 5; bottom: 5;" xml:space="preserve">
<style type="text/css">
	.st0{fill:#4B4B4B;}
</style>
<g>
	<path class="st0" d="M464.085,276.875c-20.078-25.1-41.523-46.713-41.523-46.713c-5.891-6.609-14.246-10.516-23.092-10.769
		c-8.862-0.262-17.436,3.131-23.699,9.393l-35.464,35.481c-5.865,5.848-15.368,5.848-21.218,0l-20.956-20.948
		c-2.506-2.515-6.574-2.515-9.098,0l-45.718,45.726c-2.507,2.507-2.515,6.584,0,9.098l20.947,20.947
		c5.849,5.857,5.849,15.352,0,21.218l-35.481,35.472c-6.253,6.262-9.671,14.828-9.41,23.69c0.261,8.853,4.169,17.208,10.794,23.091
		c0,0,21.606,21.436,46.705,41.523c53.483,42.79,136.403,72.21,207.271,20.054C536.295,413.27,506.866,330.366,464.085,276.875z" style="fill: rgb(75, 75, 75);"></path>
	<path class="st0" d="M267.22,227.098L164.323,124.2l8.499-45.709c3.199-17.167-2.27-34.789-14.618-47.144l-23.2-23.201
		c-2.676-2.683-5.849-4.752-9.2-6.11c-5.047-2.043-10.491-2.541-15.731-1.528c-5.224,0.996-10.339,3.604-14.348,7.63L8.129,95.725
		c-2.676,2.667-4.743,5.84-6.094,9.2c-2.042,5.046-2.54,10.481-1.527,15.723c0.996,5.233,3.604,10.339,7.63,14.356l23.2,23.21
		C43.694,170.552,61.325,176.02,78.5,172.83l45.701-8.507L227.097,267.22L267.22,227.098z M131.923,70.878l-9.579,51.466
		l-51.465,9.588c-3.688,0.691-7.469-0.49-10.119-3.14l-13.428-13.436l68.024-68.024l13.436,13.436
		C131.433,63.418,132.615,67.199,131.923,70.878z" style="fill: rgb(75, 75, 75);"></path>
</g>
</svg>
                  `
                );
              } else if (params.seriesType === 'scatter') {
                return (
                  params.name + '<br/>' + t('はずれ値') + ':' + Number(params.value[1]).toFixed(2)
                );
              }
            }
          },
          dataZoom: [
            {
              type: 'inside'
            }
          ],
          grid: {
            left: '10%',
            right: '8%',
            bottom: props.bottom,
            top: props.top
          },
          xAxis: {
            type: 'category',
            name: props.xAxisName !== undefined ? props.xAxisName : '',
            data: !isEmpty(props.xlist) ? props.xlist : props.source.xlist,
            nameTextStyle: {
              fontSize: 10
            },
            nameLocation: 'end',
            // boundaryGap: true,
            // nameGap: 10,
            splitLine: {
              show: true
            },
            axisLabel: {
              // interval: 0,
              rotate: '15',
              fontSize: 10
            }
          },
          yAxis: {
            type: 'value',
            name: props.yAxisName !== undefined ? props.yAxisName : '',
            nameTextStyle: {
              fontSize: 10
            },
            axisLabel: {
              interval: 0,
              fontSize: 10
            },
            min: scaleValue.y1AxisMin,
            max: scaleValue.y1AxisMax,
            interval: scaleValue.y1AxisInterval
          },
          series: [
            {
              name: t('X軸'),
              type: 'boxplot',
              data: boxData[0],
              color: 'rgb(243,6,6)'
            },
            {
              name: t('Y軸'),
              type: 'boxplot',
              data: boxData[1],
              color: 'rgb(30,150,50)'
            },
            {
              name: t('Z軸'),
              type: 'boxplot',
              data: boxData[2],
              color: 'rgb(0,100,255)'
            }
          ]
        }}
        exportData={exportData}
        exportFields={props.exportFields}
        exportFilename={props.exportFilename}
        height={props.height ? props.height : '100px'}
        onEvents={{
          click: (params: any) => {
            if (props.onClick != null) {
              params.mapName = props.mapName;
              params.refJobTitle = props.refJobTitle;
              params.parameterName = props.parameterName;
              props.onClick(params);
            }
          }
        }}
        inputScaleStatus={props.inputScale !== undefined ? props.inputScale : false}
        inputScaleOpen={() => setInputScaleOpen(true)}
        isShowDetailAnalysis={props.isShowDetailAnalysis ?? false}
        onClickDetailAnalysis={props.onClickDetailAnalysis ?? undefined}
      />
      <LFCChartsInputScaleDialog
        open={inputScaleOpen}
        onClose={() => setInputScaleOpen(false)}
        onApply={onApply}
        y1AxisName={props.yAxisName}
        inputY1Axis={true}
      />
    </div>
  );
};
export default LFCChartsBoxXYZ1;
